import { tt } from '@agos/shared'

import type { StockItem } from './stock-levels'

/**
 * What the stock is worth, and what it would sell for.
 *
 * **FB-078.** Legacy sums two figures over two *different* sets of items and then
 * subtracts one from the other:
 *
 * ```
 * for (const item of items) {
 *   if (m.effectiveCost != null) costValue    += item.qty * m.effectiveCost
 *   if (m.sellPrice     != null) revenueValue += item.qty * m.sellPrice
 * }
 * profit    = revenueValue - costValue
 * avgMargin = revenueValue > 0 ? (revenueValue - costValue) / revenueValue * 100 : 0
 * ```
 *
 * An item with a cost and no sell price puts its cost into the subtraction and no
 * revenue into the denominator; an item with a sell price and no cost does the
 * reverse. On the restored data the sets barely overlap at all — **3 of the 135
 * items have a cost and none has a sell price** — so legacy reports:
 *
 * | | legacy |
 * |---|---|
 * | stock value at cost | 28,70 € |
 * | potential revenue | 0,00 € |
 * | profit | **−28,70 €** |
 * | average margin | **0,0 %** |
 *
 * A profit of minus the whole cost value, beside a margin of zero. The two cannot
 * both be true, and neither is: nothing here is comparable, because no stock item is
 * linked to a product with a price.
 *
 * So the margin is computed over the **comparable set** — the items that have both —
 * and reported as *not computable* when that set is empty, rather than as `0 %`,
 * which reads like a real answer. The cost value keeps its own figure, because
 * "28,70 € of stock has a known cost, and only 3 of 135 items do" is the useful
 * sentence.
 */

export interface MarginRow {
  stockItemId?: number
  productId?: number | null
  productName?: string | null
  /** What the linked product sells for. A price, not an amount paid. */
  sellPrice?: number | null
  explicitCost?: number | null
  derivedCost?: number | null
  effectiveCost?: number | null
  marginPct?: number | null
}

export function marginsById(rows: MarginRow[]): Map<number, MarginRow> {
  const byId = new Map<number, MarginRow>()
  for (const row of rows) {
    if (row.stockItemId !== undefined) byId.set(row.stockItemId, row)
  }
  return byId
}

export interface StockValue {
  /** Σ qty × cost, over every item with a known cost. */
  costCents: number
  /** How many items that was. */
  costedCount: number
  /** Σ qty × sell price, over every item with a known price. */
  revenueCents: number
  pricedCount: number
  /** The items with both, which are the only ones a margin can be read from. */
  comparableCount: number
  comparableCostCents: number
  comparableRevenueCents: number
  /** Over the comparable set only. `null` when that set is empty. */
  marginPercent: number | null
  /** Over the comparable set only. `null` when that set is empty. */
  profitCents: number | null
  total: number
}

/**
 * A quantity times a unit price, rounded once.
 *
 * §14.7 governs amounts somebody pays; a per-unit cost is a multiplicand and
 * rounding it first invents money (FB-056). Quantities are fractional here — 8,25 kg
 * of flour — so the product is what gets rounded, and only once.
 */
export function lineValueCents(qty: number, unitPrice: number): number {
  const total = qty * unitPrice * 100
  if (!Number.isFinite(total)) return 0
  return total < 0 ? -Math.round(-total) : Math.round(total)
}

export function stockValue(items: StockItem[], margins: Map<number, MarginRow>): StockValue {
  let costCents = 0
  let revenueCents = 0
  let costedCount = 0
  let pricedCount = 0
  let comparableCount = 0
  let comparableCostCents = 0
  let comparableRevenueCents = 0

  for (const item of items) {
    const row = item.id === undefined ? undefined : margins.get(item.id)
    if (!row) continue

    const qty = item.qty ?? 0
    const cost = row.effectiveCost
    const price = row.sellPrice

    if (cost != null) {
      costCents += lineValueCents(qty, cost)
      costedCount += 1
    }
    if (price != null) {
      revenueCents += lineValueCents(qty, price)
      pricedCount += 1
    }
    if (cost != null && price != null) {
      comparableCount += 1
      comparableCostCents += lineValueCents(qty, cost)
      comparableRevenueCents += lineValueCents(qty, price)
    }
  }

  const comparable = comparableCount > 0 && comparableRevenueCents > 0

  return {
    costCents,
    costedCount,
    revenueCents,
    pricedCount,
    comparableCount,
    comparableCostCents,
    comparableRevenueCents,
    marginPercent: comparable
      ? ((comparableRevenueCents - comparableCostCents) / comparableRevenueCents) * 100
      : null,
    profitCents: comparableCount > 0 ? comparableRevenueCents - comparableCostCents : null,
    total: items.length,
  }
}

/** Why a margin could not be computed, in a sentence — or `null` when it could. */
export function marginCaveat(value: StockValue): string | null {
  if (value.marginPercent !== null) return null
  if (value.total === 0) return null
  if (value.costedCount === 0) return tt('Žiadna položka nemá zadanú nákupnú cenu.')
  if (value.pricedCount === 0) {
    return tt('Žiadna položka nie je napojená na produkt s prodejnou cenou, takže maržu nie je z čoho počítať.')
  }
  return tt('Žiadna položka nemá zároveň nákupnú aj prodejnú cenu.')
}

/** One item's margin, as the server computed it. Not recomputed here. */
export function itemMargin(item: StockItem, margins: Map<number, MarginRow>): MarginRow | null {
  if (item.id === undefined) return null
  return margins.get(item.id) ?? null
}

/** A margin percent is a rate; 12,5 % keeps its half. */
export function formatPercent(percent: number | null): string {
  if (percent === null) return '-'
  const rounded = Math.round(percent * 10) / 10
  return `${Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',')} %`
}
